/**
 * Figure S5's "Inventions (N) · granted to <Portfolio>" card.
 *
 * The inventions this entity has granted, and nothing else — one card each,
 * read-only, because the grantee never edits. The line under each name is the
 * real state of that invention: unreviewed inbound, matching not yet approved
 * by the owner, or discoverable because the owner said so.
 */
import { Link } from 'react-router-dom'
import { projectTagColor } from '../../lib/whitespace'
import type { AggregatorProject } from '../../types'

export interface EntityInventionsProps {
  projects: AggregatorProject[]
  portfolioName: string
  /** entity_id -> owner name, for the "Priya Nair · 12 documents" line. */
  ownerNameById?: Map<string, string>
}

/** The pipeline word for one granted invention, read from its real fields. */
export function inventionStage(project: AggregatorProject): 'Inbound' | 'Review' | 'Matching' | 'Intake' {
  if (project.review_status === 'pending') return 'Inbound'
  if (project.document_count === 0) return 'Intake'
  if (project.review_status === 'approved') return 'Matching'
  return 'Review'
}

const STAGE_TONE: Record<ReturnType<typeof inventionStage>, string> = {
  Inbound: 'bg-amber-100 text-amber-800',
  Review: 'bg-iflow-100 text-iflow-700',
  Matching: 'bg-emerald-100 text-emerald-700',
  Intake: 'bg-gray-100 text-gray-600',
}

/** The one true sentence under each invention — never a guess about the owner's intent. */
export function inventionNote(project: AggregatorProject): string {
  const stage = inventionStage(project)
  if (stage === 'Inbound') return 'Unreviewed — triage it'
  if (stage === 'Intake') return 'No documents yet'
  if (stage === 'Review') return 'Matching not yet approved by owner'
  return project.discoverable ? 'Discoverable — set by the owner' : 'Not discoverable — set by the owner'
}

export default function EntityInventions({ projects, portfolioName, ownerNameById }: EntityInventionsProps) {
  // The SAME id list EntityMap colours its footprints from, so a card's
  // swatch and its shape on the map can never disagree.
  const projectIds = projects.map((p) => p.project_id)
  return (
    <div data-part="EntityInventions" className="light-card p-4">
      <div className="flex items-baseline justify-between gap-3 flex-wrap">
        <div className="flex items-baseline gap-2 flex-wrap">
          <h3 className="text-[15px] font-bold text-iflow-900">Inventions ({projects.length})</h3>
          <span className="text-[12px] text-gray-500">· granted to {portfolioName || 'this portfolio'}</span>
        </div>
        <span className="text-[11.5px] text-gray-500">Read-only — the grantee never edits</span>
      </div>

      {projects.length === 0 ? (
        <p className="mt-3 text-[12.5px] text-gray-600">
          Nothing granted. Being in your portfolio shows you nothing by itself — the inventor
          decides, project by project.
        </p>
      ) : (
        <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
          {projects.map((project) => {
            const stage = inventionStage(project)
            const owner = ownerNameById?.get(project.entity_id) ?? project.entity_name
            return (
              <div
                key={project.project_id}
                data-testid="entity-invention-card"
                className="flex flex-col gap-1.5 rounded-lg border border-iflow-900/10 bg-white p-3"
              >
                <div className="flex flex-wrap items-center gap-2">
                  {/* The same colour this invention's footprint carries on the
                      entity map below, so a card and a shape on the picture
                      can be tied together by eye (figure S5). */}
                  <span
                    aria-hidden
                    data-testid="invention-swatch"
                    className="inline-block h-2.5 w-2.5 shrink-0 rounded-sm"
                    style={{ background: projectTagColor(project.project_id, projectIds) }}
                  />
                  <b className="text-[13px] text-iflow-900">{project.name}</b>
                  <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${STAGE_TONE[stage]}`}>
                    {stage}
                  </span>
                </div>
                <div className="text-[11.5px] text-gray-500">
                  {owner} · {project.document_count} document{project.document_count === 1 ? '' : 's'} ·{' '}
                  {project.match_count ?? 0} match{(project.match_count ?? 0) === 1 ? '' : 'es'}
                </div>
                <div
                  className={`text-[11.5px] ${stage === 'Inbound' ? 'font-semibold text-amber-700' : 'text-gray-500'}`}
                >
                  {inventionNote(project)}
                </div>
                <div>
                  <Link
                    to={`/project/${project.project_id}`}
                    className="inline-block rounded-lg border border-iflow-900/15 bg-white px-2.5 py-1 text-[12px] font-semibold text-iflow-800"
                  >
                    Open ▸ (read-only)
                  </Link>
                </div>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
